import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import {
  getPlanLimitV2,
  getPrice,
  getPriceWithDiscount,
  isDisabledPlan,
  planFeaturesV2,
} from '../helpers/PricingHelper';
import { AccountType } from '../../../shared/types/account/AccountType';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import { AccountResponse } from '../../../shared/models/account/AccountResponse';
import { DiscountResponseData } from '../../../shared/models/pricing/DiscountResponse';
import { ProductResponse } from '../../../shared/models/pricing/ProductResponse';
import ModelTooltips from '../../../shared/components/model-tooltips/ModelTooltips';

interface PricingBoxV2Props {
  accountResponse: AccountResponse;
  discount: DiscountResponseData;
  products: ProductResponse;
  openCheckout: (priceId: string, accountType: AccountType) => void;
}

const PricingBoxV2: React.FC<PricingBoxV2Props> = ({
  accountResponse,
  discount,
  products,
  openCheckout,
}) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<string>('month');

  return (
    <>
      {/*<div className="pricing-page__period-switcher">*/}
      {/*  <div className={`period-switcher ${'period-switcher--' + period}`}>*/}
      {/*    <Button onClick={() => setPeriod('month')} label={t('monthly')} name="month" />*/}
      {/*    <Button onClick={() => setPeriod('year')} label={t('yearly')} name="year">*/}
      {/*      <span className="period-switcher__discount">{t('saveDiscount')} 10%</span>*/}
      {/*    </Button>*/}
      {/*  </div>*/}
      {/*</div>*/}
      <div className="pricing-page__content">
        {planFeaturesV2().map((plan, index) => {
          const planSlug = plan.slug;
          const isEnterprisePlan = planSlug === 'enterprise';

          const planName = plan.name;

          const priceData = getPrice(planSlug, products, period);
          const accountType = planSlug.toUpperCase() as AccountType;
          const planLimit = getPlanLimitV2(planSlug);
          const isDisabled =
            (accountType !== 'TRIAL' &&
              accountResponse.accountType !== 'TRIAL' &&
              (accountResponse.createdMonitorCount > planLimit.maxMonitors ||
                accountResponse.usersOnAccount > planLimit.maxUsers)) ||
            accountResponse.role === 'USER' ||
            (accountResponse.hasSubscription &&
              isDisabledPlan(accountResponse.accountType, accountType, 'v2'));

          return (
            <div
              className={`price-box ${isEnterprisePlan ? 'price-box--promo' : ''}`}
              key={`product-box-${index}`}
            >
              <div className="price-box__content price-box__content--v2">
                <div className="price-box__header">
                  <label className="price-box__title">{planName}</label>
                  <div className="price-box__desc">{plan.description}</div>
                </div>

                <div className="price-box__period-price">
                  <span className="price-box__price">
                    {isEnterprisePlan ? (
                      priceData.price
                    ) : (
                      <>
                        <>{getPriceWithDiscount(products?.data, planSlug, discount, period)}</>
                        {discount && discount.status === 'active' && <s>{priceData.price}</s>}
                      </>
                    )}
                  </span>
                  <span className="price-box__period">{`${
                    priceData.priceId ? t('perMonth') : t('billedAnnually')
                  }`}</span>
                </div>

                {isDisabled ? (
                  <>
                    <Button
                      className={`btn btn--radius btn--disabled-tooltip-color`}
                      label={`${t('getStartedWith')} ${planName}`}
                      id={`pricing-${planSlug}`}
                    />
                    <CustomTooltip
                      tooltipText={
                        accountResponse.role === 'USER'
                          ? 'noPermission'
                          : accountResponse.accountType.toLowerCase().trim() ===
                            planSlug.toLowerCase().trim()
                          ? 'disableCurrentPlan'
                          : 'disableSelectPlan'
                      }
                      targetRef={`pricing-${planSlug}`}
                    />
                  </>
                ) : (
                  <Button
                    className={`btn btn--radius ${priceData.priceId ? 'btn--black' : 'btn--blue'}`}
                    label={`${t('getStartedWith')} ${planName}`}
                    onClick={() =>
                      priceData.priceId
                        ? openCheckout(priceData.priceId, accountType)
                        : (window.location.href = 'mailto:help@chatbeat.com')
                    }
                    disabled={isDisabled}
                  />
                )}
                <div>
                  <ul className="price-box__main-options">
                    {plan.main.map((opt, featureIndex) => (
                      <li key={`main-${planSlug}-${featureIndex}`}>{opt.name}</li>
                    ))}
                    <li className="price-box__llms">
                      {planSlug === 'essential'
                        ? t('llms', {
                            value: '6',
                          })
                        : t('allLlms')}
                      {plan.llms.map((opt, iconIndex) => (
                        <span
                          key={`model-icon-${planSlug}-${iconIndex}`}
                          className={`ico ico--xs ico--${opt.toLowerCase()}${
                            opt === 'AI-MODE' ? '' : '-bck'
                          }`}
                        />
                      ))}
                    </li>
                    <li>{plan.refreshInfo}</li>
                  </ul>

                  <div>
                    <div className="price-box__keys">{t('keyFeatures')}:</div>
                    <ul className="price-box__features">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={`feature-${planSlug}-${featureIndex}`}>{feature.name}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <ModelTooltips />
      </div>
    </>
  );
};

export default PricingBoxV2;
